import { z } from 'zod';

// =============================================================================
// Email Response
// =============================================================================

export const emailResponseSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  threadId: z.string().uuid(),
  integrationId: z.string().uuid().nullable(),
  provider: z.string(),
  messageId: z.string(),
  subject: z.string(),
  body: z.string().nullable(),
  bodyHtml: z.string().nullable(),
  bodyText: z.string().nullable(),
  fromEmail: z.string(),
  fromName: z.string().nullable(),
  tos: z.unknown().nullable(),
  ccs: z.unknown().nullable(),
  bccs: z.unknown().nullable(),
  priority: z.string(),
  labels: z.array(z.string()).nullable(),
  receivedAt: z.string().datetime(),
  metadata: z.unknown().nullable(),
  rfcMessageId: z.string().nullable(),
  inReplyTo: z.string().nullable(),
  emailReferences: z.string().nullable(),
  attachments: z.unknown().nullable(),
  contentHash: z.string().nullable(),
  processingStatus: z.string().nullable(),
  processedAt: z.string().datetime().nullable(),
  classification: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type EmailResponse = z.infer<typeof emailResponseSchema>;

// =============================================================================
// Email Attachment Response
// =============================================================================

export const emailAttachmentResponseSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  emailId: z.string().uuid(),
  attachmentIndex: z.number(),
  fileName: z.string(),
  contentType: z.string(),
  fileSize: z.number().nullable(),
  fileUrl: z.string().nullable(),
  contentId: z.string().nullable(),
  // Labels sorted by classification_labels.sort_order ASC. labels[0] is the primary
  // (drives tab badge + extraction routing); labels[1+] are secondary tags.
  labels: z.array(z.string()),
  classificationConfidence: z.number().nullable(),
  classificationMethod: z.string().nullable(),
  classificationReason: z.string().nullable(),
  status: z.string(),
  transcript: z.string().nullable(),
  documentId: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type EmailAttachmentResponse = z.infer<typeof emailAttachmentResponseSchema>;

// =============================================================================
// Label Update Requests — shared shape for attachment-level + email-level edits.
// Empty labels array is valid: means "no classification".
// =============================================================================

const labelUpdateBodyShape = {
  labels: z.array(z.string().min(1).max(50)).max(20),
  correctionNote: z.string().max(500).optional(),
};

export const updateAttachmentLabelsRequestSchema = z.object(labelUpdateBodyShape);
export type UpdateAttachmentLabelsRequest = z.infer<typeof updateAttachmentLabelsRequestSchema>;

export const updateEmailLabelsRequestSchema = z.object(labelUpdateBodyShape);
export type UpdateEmailLabelsRequest = z.infer<typeof updateEmailLabelsRequestSchema>;
